import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  CheckCircle2, 
  Sparkles, 
  Layers, 
  AlertTriangle, 
  Mail, 
  MessageSquare, 
  ArrowRight, 
  ExternalLink, 
  Eye, 
  Package, 
  Plus, 
  Check, 
  X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { deluxBoxes } from "../data";
import { useInquiry } from "../context/InquiryContext";
import { ScrollReveal, StaggerContainer, StaggerItem } from "./ScrollReveal";

export const DeluxBoxesSection = () => {
  const { openInquiry, getItemQuantity } = useInquiry();
  const [selectedBox, setSelectedBox] = useState(null);

  return (
    <section id="delux-kasser" className="py-16 md:py-24 bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white relative overflow-hidden">
      {/* Decorative background grid pattern & glow */}
      <div className="absolute inset-0 bg-[radial-gradient(#f97316_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-orange-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        {/* Header Block */}
        <ScrollReveal className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-orange-600/20 border border-orange-500/30 text-orange-400 text-xs font-black uppercase tracking-[0.2em] mb-4">
            <Sparkles size={14} className="text-orange-400 animate-pulse" />
            <span>Nyhed i sortimentet</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-4">
            DELUX KASSEN
          </h2>

          <p className="text-lg sm:text-xl text-zinc-300 font-medium leading-relaxed">
            Gå på opdagelse i vores populære <span className="text-orange-400 font-semibold">DELUX kasser</span> 🤩👇
          </p>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl mx-auto">
            Kasserne herunder er vist som ren inspiration til designs, farvekombinationer og specielle udformninger.
          </p>
        </ScrollReveal>

        {/* Feature Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 md:mb-16">
          <div className="bg-zinc-900/80 border border-zinc-800 p-5 flex items-start gap-4 hover:border-orange-500/50 transition-colors">
            <div className="p-2.5 bg-orange-600/20 text-orange-400 rounded-none shrink-0">
              <CheckCircle2 size={22} />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Fuld printet</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">Ingen løse sider eller samlinger – helstøbt kvalitet fra top til bund.</p>
            </div>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-5 flex items-start gap-4 hover:border-orange-500/50 transition-colors">
            <div className="p-2.5 bg-orange-600/20 text-orange-400 rounded-none shrink-0">
              <Sparkles size={22} />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Flot finish</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">Høj præcision og gennemført overflade i slidstærkt materiale.</p>
            </div>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-5 flex items-start gap-4 hover:border-orange-500/50 transition-colors">
            <div className="p-2.5 bg-orange-600/20 text-orange-400 rounded-none shrink-0">
              <Layers size={22} />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Fokus på detaljen 💯</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">Gennemførte detaljer, flotte håndtag og personlige tilpasninger.</p>
            </div>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-5 flex items-start gap-4 hover:border-orange-500/50 transition-colors">
            <div className="p-2.5 bg-orange-600/20 text-orange-400 rounded-none shrink-0">
              <Eye size={22} />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Flere designs klar 👀</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">Nye designs venter løbende på at blive godkendt og sat i produktion.</p>
            </div>
          </div>
        </div>

        {/* Section divider with sub-instruction from customer */}
        <div className="flex items-center justify-between border-b border-zinc-800 pb-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-widest text-orange-500 font-bold">Designinspiration</span>
            <h3 className="text-2xl font-bold text-white tracking-tight">Eksempler på DELUX kasser</h3>
          </div>
          <span className="text-xs text-zinc-400 hidden sm:inline-block">
            Inspirationsmodeller – kontakt os for eget design
          </span>
        </div>

        {/* 9 Boxes Gallery Grid */}
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {deluxBoxes.map((box) => {
            const quantityInList = getItemQuantity(box.id || box.name);

            return (
              <StaggerItem key={box.id}>
                <div className="bg-zinc-900 border border-zinc-800/90 hover:border-orange-500/60 transition-all duration-300 flex flex-col h-full group">
                  {/* Image container */}
                  <div className="relative aspect-4/3 w-full overflow-hidden bg-zinc-950 border-b border-zinc-800">
                    {/* Badge if selected */}
                    {quantityInList > 0 && (
                      <div className="absolute top-3 right-3 z-20 bg-orange-600 text-white text-xs font-bold px-2.5 py-1 shadow-md flex items-center gap-1.5">
                        <Check size={13} className="stroke-[3]" />
                        <span>{quantityInList} i listen</span>
                      </div>
                    )}

                    {box.tag && (
                      <div className="absolute top-3 left-3 z-10 bg-zinc-950/80 backdrop-blur-xs border border-zinc-700 text-zinc-300 text-[11px] font-semibold px-2.5 py-0.5">
                        {box.tag}
                      </div>
                    )}

                    <img
                      src={box.image}
                      alt={box.name}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                      onClick={() => setSelectedBox(box)}
                      loading="lazy"
                    />

                    <button
                      type="button"
                      onClick={() => setSelectedBox(box)}
                      aria-label={`Forstør ${box.name}`}
                      className="absolute bottom-3 right-3 bg-zinc-950/80 hover:bg-orange-600 text-white p-2 backdrop-blur-xs transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                    >
                      <Eye size={16} />
                    </button>
                  </div>

                  {/* Card content */}
                  <div className="p-5 flex flex-col flex-1 justify-between">
                    <div>
                      <h4 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors">
                        {box.name}
                      </h4>
                      <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                        {box.description}
                      </p>
                    </div>

                    <div className="mt-5 pt-4 border-t border-zinc-800/80 flex items-center justify-between gap-3">
                      <span className="text-xs font-semibold text-zinc-400 font-mono">
                        {box.price}
                      </span>

                      <button
                        type="button"
                        onClick={() => openInquiry(box)}
                        className="text-xs font-bold px-3.5 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
                      >
                        {quantityInList > 0 ? (
                          <>
                            <Plus size={13} /> Tilføj / Se liste
                          </>
                        ) : (
                          <>
                            Forespørg her <ArrowRight size={13} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>

        {/* Info & Call to Action Box under pictures */}
        <div className="bg-zinc-900 border-2 border-orange-500/40 p-6 sm:p-8 md:p-10 shadow-2xl relative overflow-hidden">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="space-y-4 max-w-2xl">
              <div className="flex items-center gap-2.5 text-amber-400 font-bold text-sm">
                <AlertTriangle size={18} className="shrink-0" />
                <span>OBS: Vi sælger ikke filer med logoer – kasserne er vist som inspiration.</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Har du spørgsmål eller et særligt ønske til din kasse?
              </h3>

              <p className="text-sm sm:text-base text-zinc-300 leading-relaxed">
                💬 Skriv til os, så finder vi den helt rigtige løsning sammen! Du kan sende en forespørgsel direkte, skrive på mail eller kontakte os via Facebook.
              </p>

              <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-zinc-400 pt-1">
                <a 
                  href="mailto:hj3ddesign@outlook.dk" 
                  className="flex items-center gap-2 text-white hover:text-orange-400 transition-colors font-medium"
                >
                  <Mail size={15} className="text-orange-500" />
                  <span>hj3ddesign@outlook.dk</span>
                </a>

                <a 
                  href="https://www.facebook.com/p/HJ3DDesign-61582796377764/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 text-white hover:text-orange-400 transition-colors font-medium"
                >
                  <MessageSquare size={15} className="text-orange-500" />
                  <span>HJ3DDesign på Facebook</span>
                  <ExternalLink size={12} className="opacity-70" />
                </a>
              </div>
            </div>

            {/* Direct Link Action Buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-auto shrink-0">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold text-sm uppercase tracking-wider text-center inline-flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-orange-600/30"
              >
                <span>Send spørgsmål til os</span>
                <ArrowRight size={16} />
              </Link>

              <Link
                to="/ydelser"
                className="px-8 py-4 bg-transparent border border-zinc-700 hover:border-zinc-500 text-white hover:bg-zinc-800 font-bold text-sm uppercase tracking-wider text-center inline-flex items-center justify-center gap-2 transition-all"
              >
                <span>Specialdesign & Idéer</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox / Zoom Modal */}
      <AnimatePresence>
        {selectedBox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelectedBox(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 max-w-2xl w-full overflow-hidden shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelectedBox(null)}
                className="absolute top-3 right-3 z-10 bg-zinc-950/80 hover:bg-zinc-800 text-white p-2 transition-colors cursor-pointer"
                aria-label="Luk"
              >
                <X size={20} />
              </button>

              <div className="relative aspect-4/3 w-full bg-zinc-950">
                <img
                  src={selectedBox.image}
                  alt={selectedBox.name}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedBox.name}</h3>
                    <p className="text-sm text-zinc-400 mt-1">{selectedBox.description}</p>
                  </div>
                  <span className="text-sm font-bold text-orange-400 font-mono shrink-0">
                    {selectedBox.price}
                  </span>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-zinc-800">
                  <Link
                    to="/kontakt"
                    onClick={() => setSelectedBox(null)}
                    className="w-full sm:w-auto text-xs font-semibold text-zinc-400 hover:text-white flex items-center justify-center gap-1.5 py-2"
                  >
                    <Mail size={14} /> Send os et spørgsmål om denne kasse
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      openInquiry(selectedBox);
                      setSelectedBox(null);
                    }}
                    className="w-full sm:w-auto px-6 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-colors"
                  >
                    <span>Forespørg på kassen</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
